import cn from 'classnames';

import ImageLazyLoader from 'components/common/ImageLazyLoader';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { makeHashFromString } from 'helpers/index';

import css from 'styles/components/landing/landingPageImage.scss';

// static media queries are ok, they align with what is returned in the data.
// this is somewhat temporary, as we redesign image components to match Figma, we'd prefer static aspect ratios rather than being so dynamic.

export const makeAspectRatioStyleInnerHtml = ({ id, mobileDimensions, tabletDimensions, desktopDimensions }) => {
  const aspect = o => {
    const stdAspect = o?.std?.x && o?.std?.y && o?.std?.x / o?.std?.y;
    const retAspect = o?.ret?.x && o?.ret?.y && o?.ret?.x / o?.ret?.y;
    return stdAspect &&
      retAspect && // if both are present
      (stdAspect < 1 && retAspect < 1
        ? Math.abs(1 / stdAspect - 1 / retAspect) > 0.1 // invert and compare if both less than one.
        : Math.abs(stdAspect - retAspect) > 0.1) // otherwise just compare
      ? undefined // aspect ratios are different,  we can not apply an aspect ratio
      : stdAspect || retAspect; // aspect ratios are the same, we can apply the aspect ratio
  };

  const mobileAspect = aspect(mobileDimensions)?.toFixed(4);
  const tabletAspect = aspect(tabletDimensions)?.toFixed(4);
  const desktopAspect = aspect(desktopDimensions)?.toFixed(4);

  if (!mobileAspect && !tabletAspect && !desktopAspect) {
    // no aspect matched - return no style
    return undefined;
  }
  const style = `
      ${
        mobileAspect
          ? `@media (max-width: 650px) {
            #${id} {
              aspect-ratio: ${mobileAspect};
              object-fit: cover;
            }
      }`
          : ''
      }

      ${
        tabletAspect
          ? `@media (min-width: 651px) and (max-width: 1024px) {
            #${id} {
              aspect-ratio: ${tabletAspect};
              object-fit: cover;
            }
      }`
          : ''
      }

      ${
        desktopAspect
          ? `@media (min-width: 1025px) {
            #${id} {
              aspect-ratio: ${desktopAspect};
              object-fit: cover;
            }
      }`
          : ''
      }
 `;

  return style;
};

const SrcSet = ({ media, srcset, bp, reg, ret }) => {
  const mediaAttribute = media ? media : `(max-width: ${bp})`;
  if (!reg && !srcset) {
    return null;
  }
  if (!srcset) {
    srcset = reg;
    if (ret) {
      srcset += `, ${ret} 2x`;
    }
  }
  return <source key={bp + reg} media={mediaAttribute} srcSet={srcset} />;
};

/**
 * Render images with different src files depending on what SiteMerch populates in Symphony
 */
export const LandingPageImage = ({
  src = null,
  srcset = null,
  mobilesrc = null,
  tabletsrc = null,
  retina = null,
  mobileretina = null,
  tabletretina = null,
  mobileSrcSetBreakpoint = '650px',
  tabletSrcSetBreakpoint = '1024px',
  sources = null,
  alt = null,
  title = null,
  width = null,
  height = null,
  role = null,
  itemProp = null,
  shouldLazyLoad = false,
  className = null,
  imgTestId = null,
  isFullWidth = false,
  mobilesrc_x: mobileX,
  mobilesrc_y: mobileY,
  tabletsrc_x: tabletX,
  tabletsrc_y: tabletY,
  src_x: srcX,
  src_y: srcY,
  retinasrc_x: retinaX,
  retinasrc_y: retinaY,
  fetchPriority
}) => {
  let pictureProps;
  if (sources?.length) {
    pictureProps = sources.map(({ media, srcset }) => <SrcSet key={srcset + media} media={media} srcset={srcset} />);
  } else {
    pictureProps = [
      // future: determine if these are MSA resizeable images.  If so, then webpify and size them.
      // future: provide a sizes parameter.
      <SrcSet bp={mobileSrcSetBreakpoint} key={mobileSrcSetBreakpoint} reg={mobilesrc} ret={mobileretina} />,
      <SrcSet bp={tabletSrcSetBreakpoint} key={tabletSrcSetBreakpoint} reg={tabletsrc} ret={tabletretina} />
    ];
  }

  const altString = !alt || alt === '""' ? '' : alt;
  const srcSet = srcset || (retina ? `${retina} 2x, ${src} 1x` : null);
  const imgProps = {
    src,
    title,
    width,
    height,
    srcSet,
    role,
    itemProp,
    'alt': altString,
    'data-test-id': imgTestId
  };

  if (src) {
    /*
     *  * if we are using mobile/tables sources, then compute the aspect ratios, otherwise, don't include them.
     *  * if we have both desktop std and retina sources, then compare the aspect ratios, and use them if they match
     *  * if we have only one std or retina source, then use that aspect ratio
     */
    const imgId = makeHashFromString(src);

    // returns null if no style, otherwise returns the aspect ratio style for the layout, if it is defined
    // if sources is provided, we can't optimize aspect ratio for mobile or tablet layouts.
    const aspectRatioStyle = makeAspectRatioStyleInnerHtml({
      id: imgId,
      mobileDimensions: { std: !sources?.length && { x: mobileX, y: mobileY } },
      tabletDimensions: { std: !sources?.length && { x: tabletX, y: tabletY } },
      desktopDimensions: {
        std: { x: srcX, y: srcY },
        ret: { x: retinaX, y: retinaY }
      }
    });

    if (shouldLazyLoad) {
      return (
        <>
          {aspectRatioStyle ? <style dangerouslySetInnerHTML={{ __html: aspectRatioStyle }} /> : undefined}
          <ImageLazyLoader
            id={imgId}
            className={cn(className, { [css.fullWidth]: isFullWidth, [css.hasStyledAspectRatio]: !!aspectRatioStyle })}
            imgProps={{ ...imgProps, id: imgId }}
            pictureProps={pictureProps}
            fetchpriority={fetchPriority}
          />
        </>
      );
    } else {
      return (
        <>
          {aspectRatioStyle ? <style dangerouslySetInnerHTML={{ __html: aspectRatioStyle }} /> : undefined}
          <picture className={isFullWidth ? css.fullWidth : null}>
            {pictureProps}
            <img
              id={imgId}
              className={cn({ [css.fullWidth]: isFullWidth, [css.hasStyledAspectRatio]: !!aspectRatioStyle })}
              src={src}
              alt={altString}
              title={title}
              width={width}
              height={height}
              srcSet={srcSet}
              role={role}
              itemProp={itemProp}
              data-test-id={imgTestId}
              fetchpriority={fetchPriority}
            />
          </picture>
        </>
      );
    }
  }
  return null;
};

export default withErrorBoundary('LandingPageImage', LandingPageImage);
